/**
 * @file        just-fetch.js
 * @author      Ryan Rossiter <ryan@kingsds.network>
 * @date        June 2020
 *
 * Cross-platform method for performing an HTTP request.
 */
'use strict';

const XMLHttpRequest = globalThis.XMLHttpRequest || require('dcp/common/dcp-xhr').XMLHttpRequest;

const kvin              = require('kvin');
const env               = require('dcp/common/dcp-env');
const { assert }        = require('dcp/common/dcp-assert');
const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
const { DcpURL }        = require('dcp/common/dcp-url');

  /**
   * Make a request, as a promise, resolves with a string or json object
   *
   * @param {string | object} url - path of file to be fetched; if object must be instanceof URL or DcpURL
   * @param {string} [type = "string"] - expected type of the response
   * @param {string} [method = "GET"] - type of request to make
   * @param {boolean} [bustCache = false] - whether to add cache poison to the end of the url
   * @param {any} [body] - what to send as the body of the message. Objects are turned into into
   *                       standard CGI key=value encoding when the method is 'POST'
   * @returns {Promise} - resolves with response (json if type="JSON", string otherwise), rejects on fail
   */
exports.justFetch = function justFetch(url, type = 'string', method = 'GET', bustCache = false, body = undefined) {
  return new Promise((resolve, reject) => {
      let deeperErrorStack = new Error().stack || '';
      deeperErrorStack = deeperErrorStack.substring(deeperErrorStack.indexOf('\n') + 1);

      if (typeof url === 'object' && typeof url.href !== 'undefined')
      {
        assert(url.protocol !== 'data:');
        url = url.href;
      }

      if (bustCache)
      {
        let bustParam = ((typeof document !== 'undefined' && document.head && document.head.getAttribute('version')) || Date.now()); /* cache buster */

        url = new URL(url);
        url.search += (url.search ? '&' : '') + encodeURI(bustParam);
        url = url.href;
      }
      /* url now guaranteed to be a string */

      const xhr = new XMLHttpRequest();
      xhr.addEventListener('loadend', function justFetch$loadendHandler() {
        try {
          xhr.removeEventListener('loadend', justFetch$loadendHandler);

          if (xhr.status >= 200 && xhr.status < 300) {
            if (xhr.getResponseHeader('content-type') && type === 'string') {
              type = xhr.getResponseHeader('content-type').split(';')[0];
            }

            let data = xhr.responseText;
            if (type === 'JSON' || type === 'application/json') {
              data = JSON.parse(data);
            }

            if (type === 'application/x-kvin' ) {
              const KVIN = new kvin.KVIN();
              KVIN.userCtors['dcpEth$$Address'] = require('../dcp-client/wallet').Address;
              KVIN.userCtors['dcpUrl$$DcpURL'] = DcpURL;
              data = KVIN.parse(data);
            }

            resolve(data);
          } else {
            function makeFetchError()
            {
              if (xhr.status)
                return new Error(`HTTP Error ${xhr.status} fetching ${url}`);

              /* NodeJS via xmlhttprequest-ssl leaves an Error duck */
              if (xhr.statusText && xhr.statusText.code && xhr.statusText.message) {
                let error = new Error(xhr.statusText.message);
                let stack = error.stack;

                Object.assign(error, xhr.statusText);
                if (xhr.statusText.stack)
                  error.stack = xhr.statusText.stack.split('\n').slice(1).join('\n') + '---\n' + stack;
                else
                  error.stack = stack;
                return error;
              }

              return new Error(`Network error fetching ${url}`);
            }

            const error = makeFetchError();

            error.request = Object.assign({}, xhr); /* Shallow clone so that console.log will work in node */
            error.request.method = method;
            error.request.location = url;
            if (deeperErrorStack)
              error.stack += '\n' + deeperErrorStack;
            if (xhr.status)
              error.code = 'HTTP_' + xhr.status;

            throw error;
          }
        } catch (e) {
          reject(e);
        }
      }); /* loadendHandler */

      xhr.open(method, url);
      if (!body)
        xhr.send();
      else
      {
        if (typeof body === 'object' && method.toUpperCase() === 'POST')
        {
          let entries = Object.entries(body);
          body = entries.map((kvp) => `${encodeURIComponent(kvp[0])}=${encodeURIComponent(kvp[1])}`).join('&');
        }

        xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
        xhr.send(body);
      }
    });
  }

/**
 * Utility function which just fetches the resource given by a given URL. This method defaults to a
 * basic GET query, however headers and body can be specified for POST / PUT / etc requests. HTTP
 * redirects are handled transparently to the caller.
 *
 * @param {URL|DcpURL|String} url      the URL to POST to. In browsers, strings which start with '/' are
 *                                     relative to window.origin.
 * @param {object}            options  object for overriding or extending default behaviours.
 *                                      - events: key-value mapping of XMLHttpRequest event names and
 *                                        corresponding event handlers
 *                                      - headers: key-value mapping of HTTP request headers and their
 *                                        values. Header names can be camel or kebab-case.
 *                                      - method: method for HTTP request
 *                                      - body: argument for xhr.send()
 *                                      - busy: function to invoke when doing network activity
 *                                      - unbusy: function to invoke when network activity stops
 *
 * @returns Promise which rejects with Error or resolves with an instance of XMLHttpRequest which had a
 *          200 status.
 */
exports.reallyJustFetch = function reallyJustFetch(url, options)
{
  const stackMemo = new Error('stackMemo');
  const method = options?.method || 'GET';

  if (typeof url === 'string' || url instanceof String)
  {
    if (env.isBrowserPlatform && url.startsWith('/'))
      url = window.location.origin + url;
    url = new URL(url);
  }
  assert(DcpURL.isURL(url));

  if ((!options || typeof options.body === 'undefined') && method !== 'GET')
    throw new Error(`Cannot fetch ${method} ${url} without a message body`);

  return new Promise(function reallyJustFetchPromiseWrapper(resolve, reject) {
    options = Object.assign({}, options, {
      __recursion: 0,
      __resolve: resolve,
      __reject: reject
    });

    try
    {
      options.busy && options.busy();
      doXHR(url);
    }
    catch(error)
    {
      reject(error);
    }
    finally
    {
      options.unbusy && options.unbusy();
    }
  });

  function doXHR(fetchUrl)
  {
    const xhr = new XMLHttpRequest();
    xhr.open(method, fetchUrl.href);

    try
    {
      for (let headerName in options.headers)
      {
        headerName = c2kCase(headerName).toLowerCase();
        xhr.setRequestHeader(headerName, options.headers[headerName]);
      }
      for (let eventName in options.events)
        xhr.addEventListener(eventName, options.events[eventName]);

      if (method !== 'GET' && !options.headers?.['content-type'])
        throw new Error(`missing content-type header for ${method} request`);

      xhr.onloadend = () => loadEndHandler(xhr, fetchUrl);
      xhr.send(options?.body);
    }
    catch(error)
    {
      xhr.abort();
      throw error;
    }
  }

  /**
   * Handle the end phase of the XHR. The top-level promise will either be rejected or resolved by the
   * end of this function. HTTP redirects are transparently handled via recursion.
   */
  function loadEndHandler(xhr, fetchUrl)
  {
    try
    {
      delete xhr.onloadend;
      for (let eventName in options.eventHandlers)
        xhr.removeEventListener(eventName, options.eventHandlers[eventName]);

      switch(xhr.status)
      {
        case 301: case 302: case 303: case 307: case 308:
        {
          const redirectUrl = new URL(xhr.getResponseHeader('location'));
          if (options.__recursion++ > 20)
            makeRejection(xhr, `excess recursion resolving redirect for ${url}`);
          else if (redirectUrl.href === fetchUrl.origin + fetchUrl.pathname)
            makeRejection(xhr, `recursive redirect ${fetchUrl.origin + fetchUrl.pathname} -> ${redirectUrl}`);
          else if (redirectUrl.href === url.origin + url.pathname)
            makeRejection(xhr, `transitively recursive redirect ${url.origin + url.pathname} -..-> ${fetchUrl.origin + fetchUrl.pathname} -> ${redirectUrl}`);
          else
          {
            redirectUrl.search = url.search;
            doXHR(redirectUrl);
          }

          return;
        }
        case 200:
        {
          options.__resolve(xhr);
          return;
        }
        default:
        {
          if (xhr.status)
            makeRejection(xhr, `status ${xhr.status} fetching ${fetchUrl.href}`);
          else if (xhr.statusText?.code && xhr.statusText?.message) /* NodeJS via xmlhttprequest-ssl leaves an Error duck */
            makeRejection(xhr, xhr.statusText.message, xhr.statusText.code)
          else
            makeRejection(xhr, `unknown error fetching ${fetchUrl.href}`);
          return;
        }
      }

      assert(false && 'unreached'); // eslint-disable-line no-unreachable
    }
    catch(error)
    {
      options.__reject(error)
    }
  }

  /**
   * Create a rejection object which is compatible with justFetchPrettyError, and
   * reject the top-level promise for this request.
   */
  function makeRejection(xhr, message, code)
  {
    if (code)
      code = `EHTTP_STATUS_${code}`;
    else
      code = `EHTTP_STATUS_${xhr.status}`;
    const rejectError = new Error(message);

    if (code)
      rejectError.code = code;
    rejectError.stack = rejectError.stack
      + '\n    --------------------'
      + '\n' + (stackMemo?.stack || '').split('\n').slice(1).join('\n');

    rejectError.request = Object.assign({}, xhr); /* Shallow clone so that console.log will work in node */
    rejectError.request.method = method;
    rejectError.request.location = url;
    options.__reject(rejectError);
  }
}

/** Reformat an error (rejection) message from utils::justFetch, so that debugging code
 *  can include (for example) a text-rendered version of the remote 404 page.
 *
 *  @param      {object}        error   The rejection from justFetch()
 *  @returns    {string}        An error message, formatted with ANSI color when the output
 *                              is a terminal, suitable for writing directly to stdout. If
 *                              the response included html content (eg a 404 page), it is
 *                              rendered to text in this string.
 */
exports.justFetchPrettyError = function utils$$justFetchPrettyError(error) {
  var headers={};
  var message = `HTTP Status: ${error.request?.status} for ${error.request?.method} ${error.request?.location}\n`;

  if (require('dcp/common/dcp-env').platform === 'pythonmonkey')
    return error.message;

  const chalk = new (requireNative('chalk').Instance)({ level: require('dcp/utils').useColour ? 1 : 0 });
  if (error.code && error.syscall)
    return chalk.yellow(error.message);

  if (!error.request || !error.request.status)
  {
    delete error.request;
    return error;
  }

  error.request.getAllResponseHeaders().replace(/\r/g,'').split('\n').forEach(function(line) {
    var colon = line.indexOf(': ')
    headers[line.slice(0,colon)] = line.slice(colon+2)
  })

  switch(headers['content-type'].replace(/;.*$/, '')) {
    case 'text/plain':
      message += '\n' + chalk.grey(error.request.responseText)
      break;
    case 'text/html': {
      let html = error.request.responseText;

      html = html.replace(/\n<a/gi, ' <a'); /* html-to-text bug, affects google 301s /wg jun 2020 */
      message += chalk.grey(require('html-to-text').convert(html, {
        wordwrap: parseInt(process.env.COLUMNS, 10) || 80,
        hideLinkHrefIfSameAsText: true,
        format: {
          heading: function (elem, fn, options) {
            var h = fn(elem.children, options);
            return '\n====\n' + chalk.yellow(chalk.bold(h.toUpperCase())) + '\n====\n';
          }
        }
      }));
      break;
    }
  }

  return message;
}

/* camel to kebab case */
function c2kCase(camel)
{
  return camel.replace(/([A-Z])/g, match => '-' + match.toLowerCase());
}
